import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/I18nContext";
import Logo from "./Logo";

export default function Header() {
  const { count, setIsOpen } = useCart();
  const { lang, setLang, supported, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);

  const mainNav = [
    { to: "/", label: t("nav.start"), key: "start" },
    { to: "/planung-bim", label: t("nav.planungBim"), key: "planung" },
    { to: "/energie-heizung", label: t("nav.energyHeat"), key: "energie" },
    { to: "/gebaeudeautomation", label: t("nav.automation"), key: "automation" },
    { to: "/wohnanlagen", label: t("nav.residential"), key: "wohnanlagen" },
    { to: "/service", label: t("nav.service"), key: "service" },
    { to: "/unternehmen", label: t("nav.company"), key: "unternehmen" },
    { to: "/kontakt", label: t("nav.contact"), key: "contact" },
  ];

  const servicesDropdown = [
    { to: "/planung-bim", label: t("svc.planning") },
    { to: "/planung-bim", label: t("svc.bim") },
    { to: "/energie-heizung", label: t("svc.heating") },
    { to: "/energie-heizung", label: t("svc.hotwater") },
    { to: "/energie-heizung", label: t("svc.cooling") },
    { to: "/energie-heizung", label: t("svc.energy") },
    { to: "/energie-heizung", label: t("svc.ems") },
    { to: "/gebaeudeautomation", label: t("svc.automation") },
    { to: "/gebaeudeautomation", label: t("svc.lighting") },
    { to: "/gebaeudeautomation", label: t("svc.shading") },
    { to: "/gebaeudeautomation", label: t("svc.access") },
    { to: "/gebaeudeautomation", label: t("svc.sensors") },
    { to: "/service", label: t("svc.commissioning") },
    { to: "/service", label: t("svc.servicecare") },
    { to: "/produktfinder", label: "Produktfinder" },
    { to: "/energie-rechner", label: "Energie-Rechner" },
    { to: "/shop", label: "Produkte & Materialien" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location]);

  useEffect(() => {
    const onClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color] duration-300 ${
        scrolled ? "bg-[#0A1F38]/96 border-white/12" : "bg-[#0A1F38]/82 border-white/10"
      }`}
    >
      <div className="site-container flex items-center justify-between h-20 gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
          <Logo size="md"/>
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-bold text-sm tracking-[0.04em] text-white whitespace-nowrap">ECO Haustechnik</div>
            <div className="text-[8px] mt-0.5 tracking-[0.28em] uppercase text-[#5FA637] font-semibold">ECO BUILDING</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-5 flex-1 justify-center">
          <NavLink
            to="/"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `text-[10px] font-semibold tracking-[0.14em] uppercase pb-1 border-b whitespace-nowrap transition-colors ${
                isActive ? "text-white border-[#5FA637]" : "text-white/70 border-transparent hover:text-white"
              }`
            }
          >
            {t("nav.start")}
          </NavLink>

          {/* Services dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              onClick={() => setServicesOpen(!servicesOpen)}
              data-testid="nav-services"
              className={`flex items-center gap-1 text-[10px] font-semibold tracking-[0.14em] uppercase pb-1 border-b border-transparent whitespace-nowrap transition-colors ${
                servicesOpen ? "text-white" : "text-white/70 hover:text-white"
              }`}
            >
              {t("nav.services")} <ChevronDown size={11} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}/>
            </button>
            {servicesOpen && (
              <div
                data-testid="services-dropdown"
                className="absolute top-full left-0 mt-4 w-[280px] bg-[#0F2A48] border border-white/15 shadow-2xl py-2 z-50"
              >
                {servicesDropdown.map((item, i) => (
                  <Link
                    key={i}
                    to={item.to}
                    onClick={() => setServicesOpen(false)}
                    className="block px-5 py-2.5 text-[11px] uppercase tracking-[0.12em] text-white/70 hover:text-[#5FA637] hover:bg-white/5 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {mainNav.slice(1).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `text-[10px] font-semibold tracking-[0.14em] uppercase pb-1 border-b whitespace-nowrap transition-colors ${
                  isActive ? "text-white border-[#5FA637]" : "text-white/70 border-transparent hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <div className="flex items-center" data-testid="lang-switcher">
            {supported.map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l.toLowerCase()}`}
                className={`lang-btn ${lang === l ? "active" : ""}`}
                aria-label={`Sprache ${l}`}
              >
                {l}
              </button>
            ))}
          </div>

          <button
            onClick={() => setIsOpen(true)}
            data-testid="header-cart-button"
            className="relative w-11 h-11 border border-white/30 hover:border-[#5FA637] hover:text-[#5FA637] flex items-center justify-center transition-colors"
            aria-label="Anfragekorb"
          >
            <ShoppingCart size={16} strokeWidth={1.6}/>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1.5 -right-1.5 bg-[#5FA637] text-[#0F2A48] text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>

          <Link to="/kontakt" data-testid="header-cta" className="btn-primary text-[10px] !py-2.5 !px-4">
            {t("cta.inquiry")}
          </Link>
        </div>

        {/* Mobile */}
        <div className="xl:hidden flex items-center gap-2">
          <button
            onClick={() => setIsOpen(true)}
            data-testid="mobile-cart-button"
            className="relative w-10 h-10 border border-white/30 flex items-center justify-center"
          >
            <ShoppingCart size={14}/>
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#5FA637] text-[#0F2A48] text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{count}</span>
            )}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="w-10 h-10 border border-white/30 flex items-center justify-center"
            data-testid="mobile-menu-toggle"
            aria-label="Menü"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden bg-[#0F2A48] border-t border-white/10 max-h-[calc(100vh-80px)] overflow-y-auto" data-testid="mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-1">
            {mainNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className="text-xs uppercase tracking-[0.18em] py-4 border-b border-white/10 text-white/80 hover:text-[#5FA637]"
                data-testid={`mobile-nav-${n.key}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/shop" className="text-xs uppercase tracking-[0.18em] py-4 border-b border-white/10 text-white/80">
              {t("nav.products")}
            </Link>

            <div className="flex items-center justify-between pt-6">
              <div className="flex items-center">
                {supported.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    data-testid={`mobile-lang-${l.toLowerCase()}`}
                    className={`lang-btn ${lang === l ? "active" : ""}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <Link to="/kontakt" className="btn-primary mt-6 justify-center" data-testid="mobile-cta">
              {t("cta.inquiry")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
